<template>
  <DashboardLayout>

    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Appraisal</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Appraisals</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Appraisals</h1>
                <p class="mb-0 small">View and manage appraisals</p>
              </div>
            </div>

            <div class="form-body">
              <!-- Filters -->
              <div class="filters-section mb-4">
                <div class="row g-3">
                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">MDA:</label>
                    <SearchableSelect v-model="filters.mda" :options="uniqueMDAs" placeholder="All" small clearable />
                  </div>

                  <!-- <div class="col-md-3">
                    <label class="form-label small fw-semibold">Date Initiated:</label>
                    <select class="form-select form-select-sm" v-model="filters.date">
                      <option value="">All Time</option>
                      <option value="this_month">This Month</option>
                      <option value="last_month">Last Month</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div> -->

                  <div class="col-md-2">
                    <label class="form-label small fw-semibold">Status:</label>
                    <select class="form-select form-select-sm" v-model="filters.status">
                      <option value="">All</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="returned">Returned</option>
                      <option value="rejected">Rejected</option>
                      <option value="recalled">Recalled</option>
                    </select>
                  </div>

                  <div class="col-md-2">
                    <label class="form-label small fw-semibold">Month:</label>
                    <select class="form-select form-select-sm" v-model.number="filters.month">
                      <option v-for="m in MONTH_OPTIONS" :key="m.value" :value="m.value">{{ m.label }}</option>
                    </select>
                  </div>

                  <div class="col-md-2">
                    <label class="form-label small fw-semibold">Year:</label>
                    <select class="form-select form-select-sm" v-model.number="filters.year">
                      <option v-for="y in YEAR_OPTIONS" :key="y" :value="y">{{ y }}</option>
                    </select>
                  </div>

                  

                  <div class="col-md-2 d-flex align-items-end">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="errorMessage" class="alert alert-warning alert-dismissible fade show" role="alert">
                {{ errorMessage }}
                <button type="button" class="btn-close" aria-label="Close" @click.prevent="clearError()"></button>
              </div>

              <!-- Table Controls -->
              <div class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box" style="max-width: 500px;">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search by Org Code, Amount Approved or Memo File Number..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                      PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                      Excel</a>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Originating MDA</th>
                        <th>Approval Authority</th>
                        <th class="text-end">Amount Approved</th>
                        <th class="text-end">Balance</th>
                        <th>Appraisal Initiation Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-if="loading">
                        <tr v-for="n in pageLength" :key="'skeleton-' + n" class="skeleton-row">
                          <td>
                            <div class="skeleton-bar skeleton-sm mx-auto"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar"></div>
                          </td>
                          <td>
                            <div class="skeleton-bar skeleton-sm"></div>
                          </td>
                        </tr>
                      </template>

                      <template v-else>
                        <tr v-if="paginatedItems.length === 0">
                          <td colspan="7" class="text-center text-muted">No appraisals to display.</td>
                        </tr>
                        <tr v-for="(appraisal, index) in paginatedItems" :key="appraisal.id"
                          :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                          <td data-label="S/N" class="serial-no text-center">
                            {{ (currentPage - 1) * pageLength + index + 1 }}
                          </td>
                          <td data-label="Originating MDA" class="primary-col">
                            <router-link
                              :to="{ name: 'ViewAppraisal', params: { id: appraisal.id }, query: { mdaid: appraisal.mdaId, year: appraisal.year } }"
                              class="view-link text-decoration-underline" @mouseenter="showTooltip($event, appraisal)"
                              @mouseleave="hideTooltip">
                              {{ appraisal.originatingMda }}
                            </router-link>
                          </td>
                          <td data-label="Approval Authority" class="secondary-col">{{ appraisal.approvalAuthority }}
                          </td>
                          <td data-label="Amount Approved" class="secondary-col amount">
                            {{ formatCurrency(appraisal.amountApproved || 0).toLocaleString() }}
                          </td>
                          <td data-label="Balance" class="secondary-col amount">
                            {{ formatCurrency(appraisal.balance || 0).toLocaleString() }}
                          </td>
                          <td data-label="Appraisal Initiation Date" class="secondary-col appraisal-date">
                            {{ formatDate(appraisal.appraisalInitiatedDate) }}
                          </td>
                          <td data-label="Status" class="secondary-col">
                            <span v-if="appraisal.warrantApprovedInd === 1">Completed</span>
                            <span v-else-if="appraisal.recalledInd === 1">Recalled</span>
                            <span v-else-if="appraisal.returnedAppraisalInd === 1">Returned</span>
                            <span v-else-if="appraisal.appraisalRejectedInd === 1">Rejected</span>
                            <span v-else>Pending</span>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Tooltip -->
              <div v-if="tooltipVisible && hoveredNotification" class="custom-tooltip" :style="{
                top: tooltipY + 'px',
                left: tooltipX + 'px'
              }">
                <div v-if="isLoadingDetails">
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading details...
                </div>
                <div v-else>
                  <p><strong>Details</strong></p>
                  <!-- APPRAISAL -->
                  <div>
                    <p><strong>MDA: </strong>{{ hoveredNotification.originatingMda || hoveredNotification.mda || 'N/A' }}</p>
                    <p><strong>Approval Authority: </strong>{{ hoveredNotification.approvalAuthority || hoveredNotification.approvalAuthorityName || 'N/A' }}</p>
                    <p><strong>Nature of Expenditure: </strong>{{ hoveredNotification.natureOfExpenditure || 'N/A' }}</p>
                    <p><strong>Purpose of Request: </strong>{{ hoveredNotification.purposeOfRequest || 'N/A' }}</p>
                    <p><strong>Amount Approved: </strong>{{ formatCurrency(hoveredNotification.amountApproved || hoveredNotification.amount || 0) }}</p>
                    <p><strong>Org Code: </strong>{{ hoveredNotification.orgCode || 'N/A' }}</p>
                    <!-- <p><strong>Balance:</strong> {{ formatCurrency(hoveredNotification.balance || 0) }}</p>
                    <p><strong>Performance:</strong> {{ Number(hoveredNotification.performance).toFixed(1) }}%</p>
                    <p><strong>Budget Year:</strong> {{ hoveredNotification.budgetYear }}</p>
                    <p><strong>Quarter:</strong> {{ hoveredNotification.quarter }}</p>
                    <p><strong>Approved Budget Amount:</strong> {{ formatCurrency(hoveredNotification.approvedBudgetAmount || 0) }}</p>
                    <p><strong>Amount Released To Date:</strong> {{ formatCurrency(hoveredNotification.amtReleasedToDate || 0) }}</p>
                    <p><strong>Approval Balance:</strong> {{ formatCurrency(hoveredNotification.approvalBalance || 0) }}</p> -->
                  </div>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, totalElements) }} of
                  {{ totalElements }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0" :class="{ 'pe-none opacity-50': loading }">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                    </li>
                    <li v-for="page in displayedPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie';
import { useRoute } from 'vue-router'
import DashboardLayout from '../layout/DashboardLayout.vue';
import SearchableSelect from '../common/SearchableSelect.vue';
import { debounce } from 'lodash'
import { useUserStore } from '@/stores/user'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css' // Import global responsive styles

const mdas = ref([])
const loading = ref(true)
const isLoadingDetails = ref(false)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL
const route = useRoute()
const userStore = useUserStore()
const hoveredNotification = ref(null)
const tooltipVisible = ref(false)
const tooltipX = ref(0)
const tooltipY = ref(0)

// backend always filters by one month (defaults to current), so no "All" option
const ALL_MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' }),
}))
const CURRENT_YEAR = new Date().getFullYear()
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - i)

const defaultFilters = () => ({
  mda: '',
  date: '',
  status: '',
  month: new Date().getMonth() + 1,
  year: CURRENT_YEAR,
})

const filters = ref(defaultFilters())

// hide future months for the current year
const MONTH_OPTIONS = computed(() => {
  if (filters.value.year < CURRENT_YEAR) return ALL_MONTHS
  return ALL_MONTHS.slice(0, new Date().getMonth() + 1)
})

// reset month if it's in the future after switching year
watch(() => filters.value.year, () => {
  const maxMonth = MONTH_OPTIONS.value.length
  if (filters.value.month > maxMonth) filters.value.month = maxMonth
})
const errorMessage = ref('')
const tableNotice = ref('')

// Official indicators: group members: 0; group head: 1; dob: 2; perm sec: 3;
// cbp: 4 (see HomePage.vue). Computed from the logged-in user's role so it's
// attached to every request regardless of status filter or entry point;
// users outside the four official offices default to 5.
const OFF_IND_BY_ROLE = {
  groupHead: 1,
  dirOfBudget: 2,
  perSec: 3,
  commOfBAndP: 4,
}

const offInd = computed(() => {
  const role = userStore.profile?.role || {}
  const matchedRole = Object.keys(OFF_IND_BY_ROLE).find(r => role[r])
  return matchedRole ? OFF_IND_BY_ROLE[matchedRole] : 5
})

const showTooltip = async (event, appraisal) => {
  hoveredNotification.value = appraisal
  tooltipVisible.value = true

  const rect = event.currentTarget.getBoundingClientRect()

  tooltipX.value = rect.left + window.scrollX
  tooltipY.value = rect.bottom + window.scrollY + 8 // small offset

  await fetchAppraisal(appraisal.id)
}

const hideTooltip = () => {
  tooltipVisible.value = false
}

const fetchAppraisal = async (id) => {
  const token = Cookies.get('auth_token')
  isLoadingDetails.value = true
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/appraisal/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    if (data.natureOfExpenditure == 2) {
      data.natureOfExpenditure = 'Recurrent';
    } else if (data.natureOfExpenditure == 3) {
      data.natureOfExpenditure = 'Capital';
    } else if (data.natureOfExpenditure == 4) {
      data.natureOfExpenditure = 'Hybrid';
    }
    hoveredNotification.value = {
      ...hoveredNotification.value,
      ...data
    }
  } catch (err) {
    console.error('Error fetching Appraisal:', err)
  } finally {
    isLoadingDetails.value = false
  }
}

// Server-side pagination: `items` only ever holds the currently cached
// batch, `batchStart` is the global 0-indexed offset of its first item, and
// `totalElements` is the backend's totalElements.
const items = ref([])
const batchStart = ref(0)
const totalElements = ref(0)
const initialized = ref(false)

// Always fetch the backend's max page size (100), regardless of the UI's
// `pageLength`, so smaller page sizes (10/25/50) stay cached across more
// pages instead of triggering a network call on every page turn.
const FETCH_SIZE = 100

// Which backend search param the current search term actually matched
// against (see resolveSearch below), and the value sent for it.
const activeSearchField = ref(null)
const activeSearchValue = ref(null)

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
}, 300))

// Drops the cached batch and re-fetches page 1 — used whenever something
// that changes what the backend returns (mdaId, status, search field)
// changes, since the old cache no longer applies.
const resetAndRefetch = async () => {
  currentPage.value = 1
  items.value = []
  batchStart.value = 0
  totalElements.value = 0
  initialized.value = false
  await ensureBatchForPage(1)
}

watch(pageLength, resetAndRefetch)

// mdaId and status are filtered server-side, so a change requires a fresh
// fetch. `date` has no backend support and stays a client-side filter
// applied only to whichever batch is currently cached.
watch(() => filters.value.mda, resetAndRefetch)
watch(() => filters.value.status, resetAndRefetch)
watch(() => [filters.value.month, filters.value.year], resetAndRefetch)

watch(() => filters.value.date, () => {
  currentPage.value = 1
})

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatCurrency = (amount) => {
  return '₦' + amount.toLocaleString('en-NG')
}

const fetchMDAs = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/mda`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    mdas.value = response.data.map(mda => ({
      id: mda.id,
      name: mda.name,
      organisation_code: mda.orgCode,
    }))
  } catch (error) {
    console.error('Failed to fetch MDAs:', error)
  }
}

const uniqueMDAs = computed(() => {
  return [...mdas.value].sort((a, b) => a.name.localeCompare(b.name))
})

const mapAppraisal = (appraisal) => ({
  id: appraisal.id,
  originatingMda: appraisal.originatingMda,
  approvalAuthority: appraisal.approvalAuthority,
  amountApproved: appraisal.amountApproved,
  balance: appraisal.balance,
  appraisalInitiatedDate: appraisal.appraisalInitiatedDate,
  warrantApprovedInd: appraisal.warrantApprovedInd,
  returnedAppraisalInd: appraisal.returnedAppraisalInd,
  appraisalRejectedInd: appraisal.appraisalRejectedInd,
  recalledInd: appraisal.recalledInd,
  mdaId: appraisal.mdaId,
  year: appraisal.year ?? appraisal.budgetYear,
  fileTrackerNumber: appraisal.fileTrackerNumber
})

// Backend statusInd values (see AppraisalController: completed=1,
// returned=2, rejected=3, pending=4, recalled=5).
const STATUS_IND_MAP = { completed: 1, returned: 2, rejected: 3, pending: 4, recalled: 5 }

// Builds the backend query params for the currently active MDA/status
// filters and search field/value.
const buildParams = () => {
  const params = {}
  if (filters.value.mda) params.mdaId = filters.value.mda
  if (activeSearchField.value) params[activeSearchField.value] = activeSearchValue.value
  const statusInd = STATUS_IND_MAP[filters.value.status]
  if (statusInd) params.statusInd = statusInd
  params.offInd = offInd.value
  params.month = filters.value.month
  params.year = filters.value.year
  return params
}

// Fetches one batch of `size` rows starting at backend page `backendPage`
// (0-indexed), caching it locally. The paginated endpoint responds 200 OK
// with a plain string body (not a Page) when there's no data for the
// group, instead of an empty page — treat that as zero results. Returns
// whether any rows matched.
const fetchAppraisalBatch = async (backendPage, size, extraParams = {}) => {
  const token = Cookies.get('auth_token')
  const isGroupScopedUser = !!(userStore.profile?.role?.groupHead || userStore.profile?.role?.groupMember)
  const groupTypeId = userStore.profile?.groupTypeId

  if (isGroupScopedUser && !groupTypeId) {
    items.value = []
    totalElements.value = 0
    return false
  }

  const basePath = `${root_api_url}/appraisal/appraisal/all`
  const url = isGroupScopedUser ? `${basePath}/${groupTypeId}` : basePath

  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      params: { page: backendPage, size, ...extraParams },
    })

    const data = response.data
    if (!data || !Array.isArray(data.content)) {
      items.value = []
      batchStart.value = backendPage * size
      totalElements.value = 0
      return false
    }

    items.value = data.content.map(mapAppraisal)
    batchStart.value = backendPage * size
    totalElements.value = data.page.totalElements
    return items.value.length > 0
  } catch (error) {
    console.error('Failed to fetch appraisal batch:', error)
    items.value = []
    totalElements.value = 0
    if (error.response && error.response.status === 404) {
      tableNotice.value = error.response.data?.errorMessage || 'No appraisals found.'
    } else {
      errorMessage.value = 'Failed to fetch appraisals. Please try again.'
    }
    return false
  }
}

// Makes sure the cached batch covers the UI page's global row range,
// fetching only when it doesn't already have it cached.
const ensureBatchForPage = async (uiPage) => {
  const pageLen = pageLength.value
  const globalStart = (uiPage - 1) * pageLen
  const globalEnd = globalStart + pageLen - 1
  const params = buildParams()

  if (!initialized.value) {
    loading.value = true
    try {
      await fetchAppraisalBatch(0, FETCH_SIZE, params)
      initialized.value = true
    } finally {
      loading.value = false
    }
    return
  }

  const cacheEnd = batchStart.value + items.value.length - 1
  const isCached = items.value.length > 0 && globalStart >= batchStart.value && globalEnd <= cacheEnd
  if (isCached) return

  loading.value = true
  try {
    const backendPage = Math.floor(globalStart / FETCH_SIZE)
    await fetchAppraisalBatch(backendPage, FETCH_SIZE, params)
  } finally {
    loading.value = false
  }
}

// A single search box can't OR across orgCode/memoFileNumber/amountApproved
// in one backend call (the endpoint combines whichever params are passed,
// it doesn't match any-of), so probe each candidate field in turn and keep
// whichever one actually returns results.
const SEARCH_FIELDS = ['orgCode', 'memoFileNumber']

const resolveSearch = async () => {
  const term = debouncedSearch.value
  currentPage.value = 1
  items.value = []
  batchStart.value = 0
  totalElements.value = 0
  initialized.value = false

  if (!term) {
    activeSearchField.value = null
    activeSearchValue.value = null
    await ensureBatchForPage(1)
    return
  }

  const attempts = SEARCH_FIELDS.map(field => ({ field, value: term }))
  // Strip currency/thousands formatting (e.g. "₦5,000" from formatCurrency)
  // before parsing, so amount searches work with values copied off the table.
  const cleanedTerm = term.replace(/[^0-9.]/g, '')
  const numericTerm = Number(cleanedTerm)
  if (cleanedTerm !== '' && !Number.isNaN(numericTerm)) {
    attempts.push({ field: 'amountApproved', value: numericTerm })
  }

  loading.value = true
  try {
    for (const attempt of attempts) {
      activeSearchField.value = attempt.field
      activeSearchValue.value = attempt.value
      // eslint-disable-next-line no-await-in-loop
      const found = await fetchAppraisalBatch(0, FETCH_SIZE, buildParams())
      initialized.value = true
      if (found) return
    }

    // Nothing matched on any field.
    activeSearchField.value = null
    activeSearchValue.value = null
  } finally {
    loading.value = false
  }
}

watch(debouncedSearch, resolveSearch)

// All currently cached rows, with the client-side-only date filter
// applied. Used for the export buttons, which — like the display below —
// only cover whatever's presently cached, not the full backend dataset.
const filteredCachedItems = computed(() => {
  let filtered = [...items.value]

  if (filters.value.date) {
    const now = new Date()
    filtered = filtered.filter(w => {
      const appraisalInitiatedDate = new Date(w.appraisalInitiatedDate)
      switch (filters.value.date) {
        case 'this_month':
          return appraisalInitiatedDate.getMonth() === now.getMonth() &&
            appraisalInitiatedDate.getFullYear() === now.getFullYear()
        case 'last_month': {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1)
          return appraisalInitiatedDate.getMonth() === lastMonth.getMonth() &&
            appraisalInitiatedDate.getFullYear() === lastMonth.getFullYear()
        }
        case 'this_year':
          return appraisalInitiatedDate.getFullYear() === now.getFullYear()
        default:
          return true
      }
    })
  }

  return [...filtered].sort((a, b) => {
    return new Date(b.appraisalInitiatedDate) - new Date(a.appraisalInitiatedDate)
  })
})

// Composes the display rows for the current UI page from the cached batch,
// then applies the same client-side date filter scoped to just this page's
// slice.
const paginatedItems = computed(() => {
  const globalStart = (currentPage.value - 1) * pageLength.value
  const globalEnd = globalStart + pageLength.value - 1
  const localStart = globalStart - batchStart.value
  const localEnd = Math.min(globalEnd, totalElements.value - 1) - batchStart.value
  const rows = items.value.slice(localStart, localEnd + 1)

  let filtered = rows

  if (filters.value.date) {
    const now = new Date()
    filtered = filtered.filter(w => {
      const appraisalInitiatedDate = new Date(w.appraisalInitiatedDate)
      switch (filters.value.date) {
        case 'this_month':
          return appraisalInitiatedDate.getMonth() === now.getMonth() &&
            appraisalInitiatedDate.getFullYear() === now.getFullYear()
        case 'last_month': {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1)
          return appraisalInitiatedDate.getMonth() === lastMonth.getMonth() &&
            appraisalInitiatedDate.getFullYear() === lastMonth.getFullYear()
        }
        case 'this_year':
          return appraisalInitiatedDate.getFullYear() === now.getFullYear()
        default:
          return true
      }
    })
  }

  return [...filtered].sort((a, b) => {
    return new Date(b.appraisalInitiatedDate) - new Date(a.appraisalInitiatedDate)
  })
})

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(totalElements.value / pageLength.value))
})

const displayedPages = computed(() => {
  const pages = []
  const maxDisplayed = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
  let endPage = Math.min(totalPages.value, startPage + maxDisplayed - 1)

  if (endPage - startPage < maxDisplayed - 1) {
    startPage = Math.max(1, endPage - maxDisplayed + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

const changePage = async (page) => {
  if (page < 1 || page > totalPages.value) return
  await ensureBatchForPage(page)
  currentPage.value = page
}

const clearFilters = () => {
  filters.value = defaultFilters()
  search.value = ''
  activeSearchField.value = null
  activeSearchValue.value = null
  resetAndRefetch()
}

const exportToPDF = () => {
  const doc = new jsPDF()
  autoTable(doc, {
    head: [['S/N', 'Originating MDA', 'Approval Authority', 'Amount Approved (₦)', 'Balance (₦)', 'Appraisal Initiation Date', 'Status']],
    body: filteredCachedItems.value.map((appraisal, index) => [
      index + 1,
      appraisal.originatingMda,
      appraisal.approvalAuthority,
      appraisal.amountApproved,
      appraisal.balance,
      appraisal.appraisalInitiatedDate,
      appraisal.warrantApprovedInd === 1 ? 'Completed' :
        appraisal.recalledInd === 1 ? 'Recalled' :
          appraisal.returnedAppraisalInd === 1 ? 'Returned' :
            appraisal.appraisalRejectedInd === 1 ? 'Rejected' : 'Pending'
    ])
  })
  doc.save('appraisals.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('appraisals')
  worksheet.addRow(['S/N', 'Originating MDA', 'Approval Authority', 'Amount Approved (₦)', 'Balance (₦)', 'Appraisal Initiation Date', 'Status'])

  filteredCachedItems.value.forEach((appraisal, index) => {
    worksheet.addRow([
      index + 1,
      appraisal.originatingMda,
      appraisal.approvalAuthority,
      appraisal.amountApproved,
      appraisal.balance,
      appraisal.appraisalInitiatedDate,
      appraisal.warrantApprovedInd === 1 ? 'Completed' :
        appraisal.recalledInd === 1 ? 'Recalled' :
          appraisal.returnedAppraisalInd === 1 ? 'Returned' :
            appraisal.appraisalRejectedInd === 1 ? 'Rejected' : 'Pending'
    ])
  })

  worksheet.getRow(1).font = { bold: true }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'appraisals.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  // MDAs don't depend on the user profile, so let that load on its own
  // timeline instead of waiting behind the profile/appraisals chain.
  const mdasPromise = fetchMDAs()

  try {
    if (!userStore.profile?.role || ((userStore.profile?.role?.groupHead || userStore.profile?.role?.groupMember) && !userStore.profile?.groupTypeId)) {
      await userStore.fetchProfile()
    }
  } catch {
    // profile fetch failed — don't block the page
  }

  if (route.query.status) {
    filters.value.status = route.query.status
  }

  if (route.query.mdaid) {
    // Wait for the MDA list so the dropdown reflects the right selection
    // type (the query param arrives as a string, mda.id may not be).
    await mdasPromise
    const matchedMda = mdas.value.find(mda => String(mda.id) === String(route.query.mdaid))
    filters.value.mda = matchedMda ? matchedMda.id : route.query.mdaid
  }

  await ensureBatchForPage(1)
  document.title = "View Appraisals"
});

</script>

<style scoped>
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

/* Component-specific styles only */
.appraisal-date {
  min-width: 150px;
}

.primary-col {
  font-weight: 500;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.skeleton-row td {
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
}

.skeleton-bar {
  height: 14px;
  width: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #eceff1 25%, #f5f6f7 37%, #eceff1 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.skeleton-bar.skeleton-sm {
  width: 24px;
}

.skeleton-bar.skeleton-lg {
  width: 90%;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0 50%;
  }
}

.filters-section {
  background-color:var(--table-bg-head);
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filters-section .row {
    gap: 0.75rem;
  }

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }

  .export-links {
    width: 100%;
    display: flex;
    gap: 1rem;
  }

  .export-link {
    flex: 1;
    text-align: center;
  }
}

.custom-tooltip {
  position: fixed;
  z-index: 9999;
  background: #ffffff;
  color: #000;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
  max-width: 280px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  animation: fadeIn 0.15s ease-in-out;
}

.custom-tooltip p {
  margin: 0 0 6px;
}

.custom-tooltip p:last-child {
  margin-bottom: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
